/**
 * This displays a single row in the PurchaseHistoryTable, but only on
 * mobile (<768px) devices as the rows need to open a modal dialog,
 * which only happens on that table and not Trips.
 */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import CmsContentRendered from "components/data/CmsContentRendered.js";
import ServerDate from 'components/ServerDate.js';
import { ALT_DATE_FORMAT } from 'utils/Constants.js';
import { centsToDisplay } from 'utils/FormatHelpers.js';
import { useModalContext } from 'context/ModalProvider.js';
import PreventDefault from 'utils/PreventDefault.js';
import PurchaseHistoryDetails from 'components/modals/PurchaseHistoryDetails.js';
import { wsPaymentHistoryIsRefund } from 'components/data/transit-account/PaymentHistory.helpers.js';

import {
	TransitProductType,
	TransitProductDescription,
	PaymentMethodCell,
	getPaymentHistoryOrigin,
} from './PurchaseHistoryTable.js';
import { Chevron } from 'components/icons/UI.js';

import * as tables from 'components/DataTable.module.css';
import * as styles from './PurchaseHistoryMobileRow.module.css';
import { isFailedPaymentStatus } from 'server/api-types/WSPaymentHistoryInfo.js';
import { isUndefined } from 'lodash';
import CmsContentRenderedInline from 'components/data/CmsContentRenderedInline.js';



const cms = {
	location: 'miscText.purchasehistory-table-location',
	paymentDeclined: 'miscText.purchasehistory-product-declined',
	amount: 'miscText.purchasehistory-table-amount',
	payment: 'miscText.purchasehistory-table-payment-method',
};


/**
 * Main display of purchase stats, used in both the regular row and
 * the top of the details modal
 */
export const PurchaseMainStats = ({ purchase }) => {
	const isRefund = wsPaymentHistoryIsRefund(purchase);
	const {
		payments,
		type,
		orderType,
	} = purchase;

	const { paymentStatus } = !isUndefined(payments) ? payments[ 0 ] : false;
	const origin = getPaymentHistoryOrigin(purchase);

	const isPaymentDeclined = isFailedPaymentStatus(paymentStatus);

	return (<>

		<div className={styles.productName}>
			{isPaymentDeclined &&
				<CmsContentRenderedInline
					contentKey={cms.paymentDeclined}
					className={styles.paymentDeclined}
					fallbackValue="Payment Declined"
				/>
			}
			<TransitProductDescription {...{
				orderLineItems: purchase?.orderLineItems,
				trips: purchase?.trips,
			}} />
		</div>

		<div className={styles.purchaseDate}>
			<ServerDate
				classNames={cx(tables.locale, styles.fadedText)}
				dateISO={purchase.paymentHistoryDateTime}
				altFormat={ALT_DATE_FORMAT}
				options={{ month: 'short', day: '2-digit', year: 'numeric' }}
			/>

			<span className={cx(styles.purchaseType, styles.fadedText)}>
				<TransitProductType {...{
					isRefund,
					type,
					orderType,
				}} />
			</span>
		</div>


		{/* Bottom row of cell, 3 columns */}
		<div className={styles.location}>
			<CmsContentRendered.P
				contentKey={cms.location}
				fallbackValue="Location"
				className={styles.fadedText}
			/>

			<p className={styles.purchaseValue}>
				{origin}
			</p>
		</div>

		<div className={styles.amount}>
			<CmsContentRendered.P
				contentKey={cms.amount}
				fallbackValue="Amount"
				className={styles.fadedText}
			/>

			<p className={styles.purchaseValue}>
				{centsToDisplay(purchase.amount)}
			</p>
		</div>

		<div className={styles.payment}>
			<CmsContentRendered.P
				contentKey={cms.payment}
				fallbackValue="Payment"
				className={styles.fadedText}
			/>

			<div className={styles.purchaseValue}>
				<PaymentMethodCell wsPaymentHistory={purchase}/>
			</div>
		</div>
	</>);
};


const PurchaseHistoryMobileRow = ({
	purchase,
	transitAccountId,
}) => {
	const { setModal } = useModalContext();
	const onModalClose = () => setModal(null);

	return (
		<div className={cx(tables.rowWrapper, styles.container)}
			onClick={PreventDefault(() => setModal(<PurchaseHistoryDetails {...{ purchase, onModalClose, transitAccountId }} />))}
			role="row"
		>
			<PurchaseMainStats {...{ purchase }} />

			<Chevron overrideClass={styles.chevron} />
		</div>);
};

PurchaseHistoryMobileRow.propTypes = {
	purchase: PropTypes.object.isRequired,
};

export default PurchaseHistoryMobileRow;
